import { type ItemId } from '../game/inventory';
import { CRAFTABLES } from '../game/craftables';
import { RESEARCH } from '../game/research';

// CRAFTING DATA. Recipes spend resources/components from your inventory; finished items are granted by
// the authority after the craft TIMER runs (see game/craftqueue.ts + the Q menu in ui/craftmenu.ts).
// Every recipe belongs to a CATEGORY so the menu can shelve them sensibly.

export interface Recipe {
  id: ItemId;
  cost: Array<{ item: ItemId; amount: number }>;
  seconds?: number; // craft time (undefined = DEFAULT_CRAFT_SECONDS)
  tier?: number; // workbench level required to craft (0/undefined = hands, craftable anywhere): must stand
  // within PROGRESSION.benchRange of a placed bench1/2/3 of >= this tier. Sourced from RESEARCH.nodes[id].tier
  // for craftable deployables (kept in one place, the /admin tech-tree editor); set inline for weapons below.
  locked?: boolean; // true = starts UNRESEARCHED: craftMenu blocks it until game/unlocks.ts marks it unlocked
  // (research_table: consume one of the item + RESEARCH.costs[id] salvage). Guns/explosives/reinforced only --
  // everything else is available the moment its bench tier is met.
}

export const DEFAULT_CRAFT_SECONDS = 3; // recipes without an explicit time

const c1 = (item: ItemId, amount: number): Recipe['cost'] => [{ item, amount }];
const cost = (...pairs: Array<[ItemId, number]>): Recipe['cost'] => pairs.map(([item, amount]) => ({ item, amount }));

// Weapons + ammo (Rust-flavoured costs). Appended to RECIPES below; also auto-appear in the /admin research palette.
// tier = the bench you must stand near; locked = also needs researching first (guns + explosives -- the
// "first-gun moment" is workbench1 -> research pistol at the table -> craft). Melee/bows are tier-gated but
// NOT locked, so the early game has a free weapon path before research exists.
const WEAPON_RECIPES: Recipe[] = [
  // melee (hands/low bench, never locked)
  { id: 'sword', cost: cost(['metal', 75], ['wood', 25], ['blade', 1]), seconds: 20, tier: 1 },
  { id: 'mace', cost: cost(['metal', 100], ['wood', 25]), seconds: 20, tier: 1 },
  { id: 'spear', cost: cost(['wood', 150], ['stone', 30]), seconds: 12 },
  { id: 'stone_spear', cost: cost(['wood', 150], ['stone', 75]), seconds: 15 },
  // bows
  { id: 'bow', cost: c1('wood', 200), seconds: 20 },
  { id: 'crossbow', cost: cost(['wood', 200], ['metal', 75]), seconds: 30, tier: 1 },
  // guns -- locked: research at the table before they craft at all
  { id: 'pistol', cost: cost(['metal', 75], ['gun_receiver', 1], ['spring', 1]), seconds: 25, tier: 1, locked: true }, // the first-gun moment: WB1 -> research -> craft
  { id: 'smg', cost: cost(['metal', 150], ['gun_receiver', 1], ['spring', 2], ['blade', 1]), seconds: 40, tier: 2, locked: true },
  { id: 'ak', cost: cost(['metal', 250], ['wood', 200], ['gun_receiver', 1], ['spring', 4]), seconds: 60, tier: 3, locked: true },
  { id: 'double_barrel', cost: cost(['metal', 100], ['gun_frame', 1], ['pipe', 1]), seconds: 30, tier: 1, locked: true },
  { id: 'shotgun', cost: cost(['metal', 200], ['gun_receiver', 1], ['spring', 2], ['pipe', 1]), seconds: 45, tier: 2, locked: true },
  { id: 'rifle', cost: cost(['metal', 250], ['long_gun_frame', 1], ['spring', 2], ['pipe', 1]), seconds: 60, tier: 3, locked: true },
  { id: 'sniper', cost: cost(['metal', 300], ['long_gun_frame', 1], ['spring', 3], ['electronics', 1]), seconds: 70, tier: 3, locked: true },
  { id: 'rocket_launcher', cost: cost(['metal', 200], ['pipe', 4], ['gun_frame', 1]), seconds: 60, tier: 3, locked: true },
  // throwables / explosives -- locked
  { id: 'grenade', cost: cost(['metal', 12], ['sulfur', 30], ['cloth', 5]), seconds: 12, tier: 2, locked: true },
  { id: 'c4', cost: cost(['metal', 20], ['sulfur', 120], ['cloth', 20], ['electronics', 2]), seconds: 30, tier: 3, locked: true },
  { id: 'satchel_charge', cost: cost(['metal', 20], ['sulfur', 60], ['rope', 1], ['cloth', 10]), seconds: 18, tier: 2, locked: true },
  // ammo (crafts one per click for now) -- unlocked with its gun's tier, never separately locked
  { id: 'arrows', cost: c1('wood', 25), seconds: 4 },
  { id: 'ammo_9mm', cost: cost(['metal', 5], ['sulfur', 5]), seconds: 3, tier: 1 },
  { id: 'ammo_554', cost: cost(['metal', 5], ['sulfur', 10]), seconds: 4, tier: 2 },
  { id: 'ammo_554_explosive', cost: cost(['metal', 5], ['sulfur', 20], ['cloth', 2]), seconds: 5, tier: 3 },
  { id: 'shotgun_shell', cost: cost(['metal', 5], ['sulfur', 8]), seconds: 4, tier: 2 },
  // Incendiary shells burn wooden doors (owner 2026-07-12) -- and give REFINED FUEL its first consumer.
  { id: 'shotgun_shell_incendiary', cost: cost(['metal', 5], ['sulfur', 8], ['refined_fuel', 2]), seconds: 5, tier: 2 },
  { id: 'rocket', cost: cost(['metal', 75], ['sulfur', 150], ['pipe', 1]), seconds: 20, tier: 3 },
];

export const RECIPES: Recipe[] = [
  // Basic tools (you spawn with a rock + torch, craft more to replace lost ones, or the build tools).
  { id: 'rock', cost: c1('stone', 5), seconds: 3 },
  { id: 'torch', cost: c1('wood', 10), seconds: 3 },
  { id: 'hammer', cost: c1('wood', 20), seconds: 5 },
  { id: 'blueprint', cost: c1('wood', 20), seconds: 5 },
  // Medical: a cheap bandage (cloth only, no workbench) stops bleeding + heals a little. A basic survival craft.
  { id: 'bandage', cost: c1('cloth', 4), seconds: 4 },
  // Tiered gather tools: axes chop wood faster, pickaxes mine ore/stone faster (see TOOLS in config).
  { id: 'wood_axe', cost: c1('wood', 30), seconds: 8 },
  { id: 'wood_pickaxe', cost: c1('wood', 30), seconds: 8 },
  { id: 'stone_axe', cost: c1('stone', 40), seconds: 10 },
  { id: 'stone_pickaxe', cost: c1('stone', 40), seconds: 10 },
  { id: 'metal_axe', cost: c1('metal', 40), seconds: 12 },
  { id: 'metal_pickaxe', cost: c1('metal', 40), seconds: 12 },
  // Storage deployables.
  { id: 'chest', cost: c1('wood', 30), seconds: 8 },
  { id: 'bigchest', cost: c1('wood', 100), seconds: 15 },
  { id: 'campfire', cost: c1('wood', 40), seconds: 6 },
  // Craftable deployables (benches, furnaces, doors/windows, traps, ...): recipe + craft time from craftables.ts,
  // tier from the RESEARCH tech tree (one source of truth -- the /admin editor tunes both at once). Reinforced
  // tier is also research-LOCKED, same rule as guns/explosives: a plate door is armor, not a starter item.
  ...CRAFTABLES.map((c): Recipe => ({
    id: c.id,
    cost: c.recipe.items.map(([item, amount]) => ({ item, amount })),
    seconds: c.recipe.seconds,
    tier: RESEARCH.nodes[c.id]?.tier,
    locked: c.id === 'reinforced_door' || c.id === 'reinforced_window',
  })),
  // Weapons + ammo.
  ...WEAPON_RECIPES,
];

export function recipeOf(id: ItemId): Recipe | undefined {
  return RECIPES.find((r) => r.id === id);
}

export function craftSeconds(r: Recipe): number {
  return r.seconds ?? DEFAULT_CRAFT_SECONDS;
}

// --- categories (the Q menu's shelf rail) --------------------------------------------------------

export type CraftCategory = 'Tools' | 'Weapons' | 'Ammo' | 'Building' | 'Stations' | 'Traps';
export const CRAFT_CATEGORIES: readonly CraftCategory[] = ['Tools', 'Weapons', 'Ammo', 'Building', 'Stations', 'Traps'];

const CAT_TOOLS = new Set<ItemId>(['rock', 'torch', 'hammer', 'blueprint', 'bandage',
  'wood_axe', 'wood_pickaxe', 'stone_axe', 'stone_pickaxe', 'metal_axe', 'metal_pickaxe']);
const CAT_AMMO = new Set<ItemId>(['arrows', 'ammo_9mm', 'ammo_554', 'ammo_554_explosive', 'shotgun_shell', 'shotgun_shell_incendiary', 'rocket']);
const CAT_WEAPONS = new Set<ItemId>(['sword', 'mace', 'spear', 'stone_spear', 'bow', 'crossbow',
  'pistol', 'smg', 'double_barrel', 'shotgun', 'ak', 'rifle', 'sniper', 'rocket_launcher', 'grenade', 'c4', 'satchel_charge']);
const CAT_BUILDING = new Set<ItemId>(['baseclaim', 'wooden_door', 'metal_door', 'metal_double_door', 'reinforced_door',
  'wood_window', 'metal_window', 'glass_window', 'reinforced_window', 'wooden_lock', 'keypad_lock']);
const CAT_TRAPS = new Set<ItemId>(['pistol_trap', 'shotgun_trap', 'flame_trap', 'spikes', 'mine']);

export function categoryOf(id: ItemId): CraftCategory {
  if (CAT_TOOLS.has(id)) return 'Tools';
  if (CAT_AMMO.has(id)) return 'Ammo';
  if (CAT_WEAPONS.has(id)) return 'Weapons';
  if (CAT_BUILDING.has(id)) return 'Building';
  if (CAT_TRAPS.has(id)) return 'Traps';
  return 'Stations'; // benches, furnaces, storage, beds, water catchers, chests, campfire, ...
}
